import * as React from 'react';
import { observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';
import Select, { SingleValue } from 'react-select';
import { CellInfo } from 'react-table';

import AirportStore from '@app/Stores/AirportStore';

import { AirportModel, CountryModel } from '@app/Models/WebApiModels';
import { TextCell } from '@app/Components/CellFormatters/TextCell';
import { ViewMode } from '@app/Components/CellFormatters/ViewMode';

type InlineAirportsDropdownCellProps<T extends {id: string}> = {
    value: string;
    row: T;
    fieldKey: keyof T;
    onChange: Function;
    cellInfo: CellInfo;
    isReadOnly?: boolean;
    airportStore: AirportStore;
};

@observer
export class InlineAirportsDropdownCell<T extends {id: string}> extends React.Component<InlineAirportsDropdownCellProps<T>, {}> {
    store: InlineAirportsDropdownCellStore<T>;
    container: React.RefObject<HTMLDivElement> = React.createRef();
    table: Element | null = null;

    constructor(props: InlineAirportsDropdownCellProps<T>) {
        super(props);
        this.store = new InlineAirportsDropdownCellStore(props);
    }

    componentDidUpdate(prevProps: InlineAirportsDropdownCellProps<T>) {
        if (this.props.row.id !== prevProps.row.id || this.props !== prevProps) {
            this.store.updateProps(this.props);
        }
    }

    componentDidMount() {
        document.body.addEventListener('scroll', this.handleScroll);
        if (this.container.current) {
            this.table = this.container.current.closest('.rt-tbody');
        }
        if (this.table) {
            this.table.addEventListener('scroll', this.handleScroll);
        }
    }

    componentWillUnmount() {
        document.body.removeEventListener('scroll', this.handleScroll);
        if (this.table) {
            this.table.addEventListener('scroll', this.handleScroll);
        }
    }

    handleScroll = () => {
        this.store.toViewMode();
    };

    render() {
        return (
            <div ref={this.container}>
                {this.renderContent()}
            </div>
        );
    }

    renderContent() {
        if (this.store.mode === ViewMode.view) {
            return <TextCell value={this.store.selectedName} onClick={this.store.toEditMode} />;
        } else {
            return (
                <Select
                    isMulti={false}
                    options={this.props.airportStore.airportSearchableOptions}
                    placeholder={this.store.selected}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    menuPosition={'fixed'}
                    menuPlacement="bottom"
                    onMenuClose={() => this.store.toViewMode()}
                    onChange={this.store.onChange}
                    autoFocus
                    defaultMenuIsOpen={true}
                    classNamePrefix="select-filter"
                    className="select-filter-container"
                    getOptionLabel={option => option.value + ' - ' + option.label}
                />
            );
        }
    }
}

class InlineAirportsDropdownCellStore<T extends {id: string}> {
    @observable selected: string;
    @observable mode: string = ViewMode.view;
    @observable _initValue: string;

    private _currentProps: InlineAirportsDropdownCellProps<T>;
    private readonly _entity: string = 'function';
    private _airportStore: AirportStore;
    
    constructor(props: InlineAirportsDropdownCellProps<T>) {
        this.updateProps(props);
    }

    @action
    toEditMode = () => {
        if (this._currentProps.isReadOnly) return;
        this.mode = ViewMode.edit;
    };

    @action
    toViewMode = () => {
        this.mode = ViewMode.view;
    };

    @action
    onChange = (selectedOption: SingleValue<CountryModel>) => {
        this.mode = ViewMode.view;
        if (selectedOption && !Array.isArray(selectedOption)) {
            const selected = selectedOption as CountryModel;
            if (this.selected !== selected.value) {
                this.selected = '' + selected.value;
                const row = this._currentProps.row as unknown as {[key: string]: string};
                row[this._currentProps.fieldKey as string] = this.selected;
                this._currentProps.onChange(this._currentProps.row, this._entity);
            }
        }
    };

    @computed
    get selectedName() {
        if (this.selected) {
            const selected = this._airportStore.airports.find((airport: AirportModel) => airport.code === this.selected);
            return selected && selected.name || '';
        }
    }

    @action
    updateProps(props: InlineAirportsDropdownCellProps<T>) {
        this._initValue = props.value || '';
        this._currentProps = { ...props };
        this.selected = props.value;
        this._airportStore = props.airportStore;
    }
}
